/**
 * Gabarits de supports de cours (recette du dirigeant du 31/10/2026 : « travailler sur des templates de support de
 * cours, pour avoir des visuels et des slides les plus aboutis possibles. Les visuels générés viennent en support »).
 *
 * Quatre gabarits (Classique pro, Moderne vif, Atelier pratique, Immersif 3D), chacun décliné selon le thème effectif
 * (style + charte, `domaine/visuels/charte.ts`), et quinze MISES EN PAGE soignées : couverture, sommaire, objectifs,
 * chapitre, notion clé, visuel pleine page, comparaison, chiffres clés, processus, à retenir, activité, quiz éclair,
 * synthèse, sources, fin.
 *
 * Ce module est PUR : il compose une diapositive en « scène » (formes, textes, images référencées, en pouces sur
 * 13,333 × 7,5) ; le serveur la traduit en PPTX natif (texte éditable) et en SVG (vignettes, aperçus). Une seule
 * description, deux rendus identiques.
 *
 * Règles de mise en page (Mayer, Reynolds « Presentation Zen », grilles de 12 colonnes) : marges de 0,7 po ; titre
 * en haut à gauche, 28 à 34 pt ; corps ≥ 16 pt ; une idée par diapositive, ~35 mots au plus (au-delà, le corps
 * diminue et le plan signale la surcharge) ; visuels dans des emplacements prévus (jamais posés en vrac) ; notes du
 * formateur dans les notes de la diapositive.
 */
import type { Diapo, TypeDiapo } from "../pedagogie/parcours";
import { assombrir, couleurSerie, eclaircir, melanger, pastille, texteSur, type IdGabarit, type Theme } from "../visuels/charte";
import { pictoPour, type Picto } from "../visuels/pictos";

export const L = 13.333;
export const H = 7.5;
const MX = 0.7;

// ——— Mises en page ———

export const MISES_EN_PAGE = ["couverture", "sommaire", "objectifs", "chapitre", "notion_cle", "visuel_plein", "comparaison", "chiffres_cles", "processus", "a_retenir", "activite", "quiz", "synthese", "sources", "fin"] as const;
export type MiseEnPage = (typeof MISES_EN_PAGE)[number];

export const LIBELLES_MISE_EN_PAGE: Record<MiseEnPage, string> = {
  couverture: "Couverture",
  sommaire: "Sommaire / plan",
  objectifs: "Objectifs du module",
  chapitre: "Chapitre (intercalaire)",
  notion_cle: "Notion clé (texte + visuel)",
  visuel_plein: "Visuel pleine page",
  comparaison: "Comparaison 2 colonnes",
  chiffres_cles: "Chiffres clés",
  processus: "Processus / étapes",
  a_retenir: "À retenir",
  activite: "Activité / exercice",
  quiz: "Quiz éclair",
  synthese: "Synthèse",
  sources: "Sources",
  fin: "Fin / contact",
};

/** Mise en page par défaut d'un type de diapositive (plans anciens ou saisis à la main). */
export const MISE_EN_PAGE_PAR_TYPE: Record<TypeDiapo, MiseEnPage> = {
  titre: "couverture",
  objectifs: "objectifs",
  sommaire: "sommaire",
  amorce: "chapitre",
  notion: "notion_cle",
  schema: "visuel_plein",
  exemple: "notion_cle",
  point_etape: "quiz",
  pratique: "activite",
  debriefing: "a_retenir",
  vigilance: "a_retenir",
  synthese: "synthese",
  quiz: "quiz",
};

export const MOTS_MAX_DIAPO = 35;
/** Nombre de mots affichés (titre + points + colonnes + chiffres). */
export function motsDiapo(d: Pick<Diapo, "titre" | "points"> & Partial<Pick<Diapo, "colonnes" | "chiffres">>): number {
  const textes = [d.titre, ...d.points, ...(d.colonnes ?? []).flatMap((c) => [c.titre, ...c.points]), ...(d.chiffres ?? []).flatMap((c) => [c.valeur, c.libelle])];
  return textes.join(" ").split(/\s+/).filter(Boolean).length;
}
/** Diapositives trop denses (« une idée par diapo, ~35 mots au plus ») : numéros à partir de 1. */
export const diaposDenses = (diapos: readonly Diapo[]) => diapos.flatMap((d, i) => (d.mise_en_page === "sources" || motsDiapo(d) <= MOTS_MAX_DIAPO ? [] : [i + 1]));

// ——— Scène ———

export type Element =
  | { t: "rect"; x: number; y: number; w: number; h: number; fond: string; rayon?: number; contour?: string; epaisseur?: number; opacite?: number; ombre?: boolean; rotation?: number }
  | { t: "ellipse"; x: number; y: number; w: number; h: number; fond: string; contour?: string; epaisseur?: number; opacite?: number }
  | {
      t: "texte";
      x: number;
      y: number;
      w: number;
      h: number;
      texte: string | string[];
      taille: number;
      couleur: string;
      police: string;
      gras?: boolean;
      italique?: boolean;
      align?: "left" | "center" | "right";
      valign?: "top" | "middle" | "bottom";
      puces?: "rond" | "numero";
      espacement?: number;
      lien?: string;
      rotation?: number;
    }
  | { t: "image"; x: number; y: number; w: number; h: number; ref: string; alt?: string; rayon?: number }
  | { t: "ligne"; x1: number; y1: number; x2: number; y2: number; couleur: string; epaisseur: number; pointille?: boolean; fleche?: boolean }
  | { t: "picto"; x: number; y: number; taille: number; picto: Picto; couleur: string };

export interface Scene {
  fond: string;
  /** Décor plein cadre (référence d'une image produite par le serveur à partir de `decorSvg`). */
  decor?: string;
  elements: Element[];
  notes: string;
}

export interface ContexteSupport {
  formation_titre: string;
  module_titre: string;
  rang: number;
  duree: string;
  organisme: string;
  contact: string;
  logo: boolean;
}

export interface Page {
  mise_en_page: MiseEnPage;
  diapo: Diapo;
  /** Index du visuel placé dans l'emplacement prévu (référence `visuel:N`). */
  visuel?: number;
  legende?: string;
}

export interface SourcePage {
  rang: number;
  titre: string;
  url: string;
  editeur: string;
}

// ——— Gabarits ———

export interface Gabarit {
  id: IdGabarit;
  nom: string;
  description: string;
  /** Fond sombre quel que soit le style (immersif). */
  sombre: boolean;
  rayon: number;
  /** Cartes : blanches à ombre, teintées (post-it) ou translucides sombres. */
  cartes: "ombre" | "contour" | "postit" | "verre";
  taille_titre: number;
}

export const GABARITS: Record<IdGabarit, Gabarit> = {
  "classique-pro": { id: "classique-pro", nom: "Classique pro", description: "Bandeau latéral, grille sobre, beaucoup d'air : pour tous les publics.", sombre: false, rayon: 0.08, cartes: "contour", taille_titre: 30 },
  "moderne-vif": { id: "moderne-vif", nom: "Moderne vif", description: "Dégradés, formes organiques et cartes colorées : le gabarit par défaut.", sombre: false, rayon: 0.18, cartes: "ombre", taille_titre: 32 },
  "atelier-pratique": { id: "atelier-pratique", nom: "Atelier pratique", description: "Esprit atelier : post-it, rubans, gros pictogrammes, centré sur l'action.", sombre: false, rayon: 0.06, cartes: "postit", taille_titre: 30 },
  "immersif-3d": { id: "immersif-3d", nom: "Immersif 3D", description: "Fond nuit, volumes isométriques et néons : visuels pleine page, idéal en projection.", sombre: true, rayon: 0.16, cartes: "verre", taille_titre: 32 },
};

/** Couleurs du support : celles du thème, en version sombre pour l'immersif. */
export function couleursSupport(g: Gabarit, t: Theme) {
  const sombre = g.sombre || t.style.sombre;
  const fond = sombre ? (t.style.sombre ? t.c.fond : melanger(assombrir(t.c.primaire, 0.82), "#0B1026", 0.5)) : g.id === "atelier-pratique" ? melanger(t.c.fond, "#FFF4E0", 0.6) : t.style.sombre ? "#FFFFFF" : t.c.fond === "#FFFFFF" ? "#FFFFFF" : eclaircir(t.c.fond, 0.4);
  const encre = sombre ? "#F3F5FF" : t.style.sombre ? "#16163A" : t.c.encre;
  const surface = sombre ? melanger(fond, "#FFFFFF", 0.08) : "#FFFFFF";
  const douce = sombre ? "#C9D0EE" : melanger(encre, "#FFFFFF", 0.32);
  return { sombre, fond, encre, surface, douce, primaire: t.c.primaire, accent: t.c.accent, titre: sombre ? "#FFFFFF" : t.style.sombre ? assombrir(t.c.primaire, 0.4) : encre };
}

// ——— Placement des visuels et pagination ———

const mots = (t: string) => new Set(t.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").split(/[^a-z0-9]+/).filter((m) => m.length > 3));

/**
 * Place chaque visuel dans l'emplacement prévu de la diapositive la plus proche par le sens (mots communs entre la
 * notion du visuel et le titre, les points et le visuel suggéré de la diapositive) parmi les « notion clé » et
 * « visuel pleine page » libres ; à défaut, une page « visuel pleine page » est insérée avant la synthèse.
 */
export function paginer(diapos: readonly Diapo[], visuels: ReadonlyArray<{ titre: string; notion: string; legende: string }>, sources: readonly SourcePage[] = []): Page[] {
  const pages: Page[] = diapos.map((d) => ({ mise_en_page: d.mise_en_page ?? MISE_EN_PAGE_PAR_TYPE[d.type], diapo: d }));
  const libres = (p: Page) => (p.mise_en_page === "notion_cle" || p.mise_en_page === "visuel_plein") && p.visuel === undefined;
  visuels.forEach((v, i) => {
    const cible = mots(`${v.notion} ${v.titre}`);
    let meilleur = -1;
    let score = 0;
    pages.forEach((p, k) => {
      if (!libres(p)) return;
      const m = mots(`${p.diapo.titre} ${p.diapo.points.join(" ")} ${p.diapo.visuel}`);
      const s = [...cible].filter((x) => m.has(x)).length + (p.mise_en_page === "visuel_plein" ? 0.5 : 0);
      if (s > score) [meilleur, score] = [k, s];
    });
    if (meilleur >= 0 && score >= 1) {
      pages[meilleur]!.visuel = i;
      pages[meilleur]!.legende = v.legende;
    } else {
      const avant = pages.findIndex((p) => p.mise_en_page === "synthese");
      const d: Diapo = { type: "schema", titre: v.titre, points: [], visuel: "", notes: `Visuel : ${v.titre}. Faites décrire le schéma par les stagiaires avant de le commenter (récupération en mémoire).`, mise_en_page: "visuel_plein" };
      pages.splice(avant >= 0 ? avant : pages.length, 0, { mise_en_page: "visuel_plein", diapo: d, visuel: i, legende: v.legende });
    }
  });
  // Un « visuel pleine page » sans visuel devient une notion clé (emplacement suggéré, à compléter par le formateur).
  for (const p of pages) if (p.mise_en_page === "visuel_plein" && p.visuel === undefined) p.mise_en_page = "notion_cle";
  // Comparaison sans colonnes, chiffres clés sans chiffres : repli sur une notion clé.
  for (const p of pages) {
    if (p.mise_en_page === "comparaison" && !(p.diapo.colonnes?.length === 2) && p.diapo.points.length < 2) p.mise_en_page = "notion_cle";
    if (p.mise_en_page === "chiffres_cles" && !((p.diapo.chiffres?.length ?? 0) >= 2)) p.mise_en_page = "notion_cle";
  }
  if (sources.length) pages.push({ mise_en_page: "sources", diapo: { type: "synthese", titre: "Sources", points: [], visuel: "", notes: "", mise_en_page: "sources" } });
  pages.push({ mise_en_page: "fin", diapo: { type: "synthese", titre: "Merci !", points: [], visuel: "", notes: "Recueillez les questions et rappelez la suite du parcours.", mise_en_page: "fin" } });
  return pages;
}

// ——— Composition d'une page ———

const ETIQUETTES: Partial<Record<TypeDiapo, string>> = {
  amorce: "ÉCHANGE",
  point_etape: "POINT D'ÉTAPE",
  pratique: "MISE EN PRATIQUE",
  debriefing: "DÉBRIEFING",
  vigilance: "POINT DE VIGILANCE",
  synthese: "À RETENIR",
  quiz: "QUIZ ÉCLAIR",
  exemple: "EXEMPLE",
  schema: "VISUALISATION",
  notion: "NOTION CLÉ",
  objectifs: "OBJECTIFS",
  sommaire: "AU PROGRAMME",
};

/** Corps du texte selon le nombre de mots (densité) : 22 pt aéré → 16 pt dense. */
const corpsPour = (n: number, base = 20) => (n <= 18 ? base + 2 : n <= 30 ? base : n <= 45 ? base - 2 : Math.max(16, base - 4));

interface Ctx {
  g: Gabarit;
  t: Theme;
  k: ReturnType<typeof couleursSupport>;
  els: Element[];
  c: ContexteSupport;
  numero: number;
  total: number;
}

const tp = (ctx: Ctx) => ctx.t.police_titre;
const pt = (ctx: Ctx) => ctx.t.police_texte;

function carte(ctx: Ctx, x: number, y: number, w: number, h: number, i: number, o: { couleur?: string; teinte?: boolean } = {}): void {
  const { g, k, t } = ctx;
  const c = o.couleur ?? couleurSerie(t, i);
  switch (g.cartes) {
    case "ombre":
      ctx.els.push({ t: "rect", x, y, w, h, fond: o.teinte ? eclaircir(c, 0.88) : k.surface, rayon: g.rayon, ombre: true });
      ctx.els.push({ t: "rect", x, y, w, h: 0.09, fond: c, rayon: 0.04 });
      break;
    case "contour":
      ctx.els.push({ t: "rect", x, y, w, h, fond: o.teinte ? eclaircir(c, 0.9) : k.surface, rayon: g.rayon, contour: melanger(c, "#FFFFFF", 0.45), epaisseur: 1.25 });
      ctx.els.push({ t: "rect", x, y: y + 0.12, w: 0.07, h: h - 0.24, fond: c });
      break;
    case "postit":
      ctx.els.push({ t: "rect", x, y, w, h, fond: eclaircir(c, 0.78), rayon: g.rayon, ombre: true, rotation: i % 2 === 0 ? -1 : 1 });
      ctx.els.push({ t: "rect", x: x + w / 2 - 0.45, y: y - 0.12, w: 0.9, h: 0.26, fond: melanger(c, "#FFFFFF", 0.35), opacite: 0.85, rotation: i % 2 === 0 ? 3 : -3 });
      break;
    case "verre":
      ctx.els.push({ t: "rect", x, y, w, h, fond: k.surface, rayon: g.rayon, contour: c, epaisseur: 1.75, opacite: 0.92 });
      break;
  }
}

function pastilleRonde(ctx: Ctx, cx: number, cy: number, d: number, i: number, o: { picto?: Picto | null; texte?: string; couleur?: string } = {}): void {
  const c = o.couleur ?? couleurSerie(ctx.t, i);
  const p = pastille(c, "#16163A");
  ctx.els.push({ t: "ellipse", x: cx - d / 2, y: cy - d / 2, w: d, h: d, fond: ctx.g.cartes === "verre" ? ctx.k.surface : p.fond, contour: ctx.g.cartes === "verre" ? c : undefined, epaisseur: 2 });
  const encre = ctx.g.cartes === "verre" ? c : p.texte;
  if (o.picto) ctx.els.push({ t: "picto", x: cx - d * 0.3, y: cy - d * 0.3, taille: d * 0.6, picto: o.picto, couleur: encre });
  else ctx.els.push({ t: "texte", x: cx - d / 2, y: cy - d / 2, w: d, h: d, texte: o.texte ?? String(i + 1), taille: Math.round(d * 30), couleur: encre, police: tp(ctx), gras: true, align: "center", valign: "middle" });
}

/** Étiquette + titre de la diapositive (zone haute), et décor propre au gabarit. */
function entete(ctx: Ctx, titre: string, etiquette?: string, couleur?: string): number {
  const { g, k, t } = ctx;
  const x = g.id === "classique-pro" ? MX + 0.2 : MX;
  let y = 0.45;
  if (etiquette) {
    const c = couleur ?? t.c.primaire;
    const p = pastille(c, "#16163A");
    const w = Math.max(1.6, etiquette.length * 0.115 + 0.5);
    if (g.id === "atelier-pratique") ctx.els.push({ t: "rect", x: x - 0.1, y, w, h: 0.36, fond: p.fond, rotation: -2 });
    else ctx.els.push({ t: "rect", x, y, w, h: 0.34, fond: g.cartes === "verre" ? k.surface : p.fond, rayon: 0.17, contour: g.cartes === "verre" ? c : undefined, epaisseur: 1.5 });
    ctx.els.push({ t: "texte", x: x - (g.id === "atelier-pratique" ? 0.1 : 0), y, w, h: g.id === "atelier-pratique" ? 0.36 : 0.34, texte: etiquette, taille: 11, couleur: g.cartes === "verre" ? c : p.texte, police: tp(ctx), gras: true, align: "center", valign: "middle", espacement: 1.5 });
    y += 0.5;
  }
  ctx.els.push({ t: "texte", x, y, w: L - x - MX - (g.id === "moderne-vif" ? 0.9 : 0), h: 0.95, texte: titre, taille: titre.length > 60 ? g.taille_titre - 6 : g.taille_titre, couleur: k.titre, police: tp(ctx), gras: true, valign: "top" });
  if (g.id === "moderne-vif") ctx.els.push({ t: "rect", x, y: y + 0.98, w: 0.9, h: 0.08, fond: t.c.accent, rayon: 0.04 });
  return y + 1.2;
}

function pied(ctx: Ctx): void {
  const { k, c } = ctx;
  const x = ctx.g.id === "classique-pro" ? MX + 0.2 : MX;
  ctx.els.push({ t: "texte", x, y: 6.98, w: 9, h: 0.32, texte: `${c.formation_titre} · Module ${c.rang}`, taille: 10, couleur: k.douce, police: pt(ctx) });
  ctx.els.push({ t: "texte", x: L - MX - 1.6, y: 6.98, w: 1.6, h: 0.32, texte: `${ctx.numero} / ${ctx.total}`, taille: 10, couleur: k.douce, police: pt(ctx), align: "right" });
  if (c.logo && ctx.g.id !== "immersif-3d") ctx.els.push({ t: "image", x: L - MX - 2.6, y: 6.96, w: 0.9, h: 0.36, ref: "logo" });
}

function listePoints(ctx: Ctx, x: number, y: number, w: number, h: number, points: string[], o: { taille?: number; numero?: boolean } = {}): void {
  if (!points.length) return;
  ctx.els.push({ t: "texte", x, y, w, h, texte: points.slice(0, 6), taille: o.taille ?? corpsPour(points.join(" ").split(/\s+/).length), couleur: ctx.k.encre, police: pt(ctx), puces: o.numero ? "numero" : "rond", valign: "top" });
}

/** Emplacement de visuel : l'image du visuel placé, sinon un cadre « visuel suggéré » avec pictogramme. */
function emplacementVisuel(ctx: Ctx, p: Page, x: number, y: number, w: number, h: number): void {
  if (p.visuel !== undefined) {
    ctx.els.push({ t: "rect", x: x - 0.06, y: y - 0.06, w: w + 0.12, h: h + 0.12, fond: ctx.k.surface, rayon: ctx.g.rayon, ombre: ctx.g.cartes !== "verre", contour: ctx.g.cartes === "verre" ? ctx.t.c.primaire : undefined, epaisseur: 1.5 });
    ctx.els.push({ t: "image", x, y, w, h, ref: `visuel:${p.visuel}`, alt: p.diapo.titre, rayon: ctx.g.rayon });
    return;
  }
  const c = ctx.t.c.secondaire;
  ctx.els.push({ t: "rect", x, y, w, h, fond: ctx.k.sombre ? ctx.k.surface : eclaircir(c, 0.88), rayon: ctx.g.rayon, contour: melanger(c, "#FFFFFF", 0.3), epaisseur: 1.25 });
  const picto = pictoPour("", p.diapo.visuel, p.diapo.titre) ?? "idee";
  pastilleRonde(ctx, x + w / 2, y + h * 0.32, Math.min(1.3, h * 0.36), 1, { picto, couleur: c });
  ctx.els.push({ t: "texte", x: x + 0.3, y: y + h * 0.56, w: w - 0.6, h: h * 0.38, texte: p.diapo.visuel || "Emplacement de visuel", taille: 14, italique: true, couleur: ctx.k.sombre ? ctx.k.douce : ctx.t.c.encre_douce, police: pt(ctx), align: "center", valign: "top" });
}

type Compositeur = (ctx: Ctx, p: Page, sources: readonly SourcePage[]) => void;

const COMPOSITEURS: Record<MiseEnPage, Compositeur> = {
  couverture(ctx) {
    const { k, c, g, t } = ctx;
    const clair = g.id === "atelier-pratique";
    const encre = clair ? k.encre : "#FFFFFF";
    const x = g.id === "classique-pro" ? 1.0 : MX + 0.1;
    const w = g.id === "immersif-3d" ? 7.2 : 8.6;
    const tag = pastille(clair ? t.c.primaire : "#FFFFFF", t.c.encre);
    ctx.els.push({ t: "rect", x, y: 1.35, w: 1.9, h: 0.42, fond: clair ? tag.fond : "#FFFFFF", rayon: 0.21, opacite: clair ? 1 : 0.95 });
    ctx.els.push({ t: "texte", x, y: 1.35, w: 1.9, h: 0.42, texte: `MODULE ${c.rang}`, taille: 13, gras: true, couleur: clair ? tag.texte : assombrir(t.c.primaire, 0.2), police: tp(ctx), align: "center", valign: "middle", espacement: 2 });
    ctx.els.push({ t: "texte", x, y: 1.95, w, h: 2.4, texte: c.module_titre, taille: c.module_titre.length > 50 ? 36 : 44, gras: true, couleur: encre, police: tp(ctx), valign: "top" });
    ctx.els.push({ t: "texte", x, y: 4.45, w, h: 0.6, texte: c.formation_titre, taille: 20, couleur: encre, police: pt(ctx) });
    if (c.duree) ctx.els.push({ t: "texte", x, y: 5.05, w, h: 0.45, texte: `Durée : ${c.duree}`, taille: 15, couleur: encre, police: pt(ctx) });
    ctx.els.push({ t: "texte", x, y: 6.55, w: 6, h: 0.4, texte: c.organisme, taille: 13, gras: true, couleur: encre, police: pt(ctx) });
    if (c.logo) ctx.els.push({ t: "image", x: L - MX - 2.0, y: 6.3, w: 1.8, h: 0.75, ref: "logo" });
  },
  chapitre(ctx, p) {
    const { k, g, t } = ctx;
    const clair = g.id === "atelier-pratique";
    const encre = clair ? k.encre : "#FFFFFF";
    const etiquette = ETIQUETTES[p.diapo.type] ?? "CHAPITRE";
    ctx.els.push({ t: "texte", x: MX + 0.3, y: 1.7, w: 8.8, h: 0.5, texte: etiquette, taille: 15, gras: true, couleur: clair ? t.c.primaire_texte : encre, police: tp(ctx), espacement: 3 });
    ctx.els.push({ t: "texte", x: MX + 0.3, y: 2.3, w: 8.6, h: 2.4, texte: p.diapo.titre, taille: p.diapo.titre.length > 60 ? 34 : 40, gras: true, couleur: encre, police: tp(ctx), valign: "top" });
    if (p.diapo.points.length) ctx.els.push({ t: "texte", x: MX + 0.3, y: 4.8, w: 8.4, h: 1.6, texte: p.diapo.points.slice(0, 2).join(" — "), taille: 18, couleur: encre, police: pt(ctx), valign: "top" });
  },
  sommaire(ctx, p) {
    const y0 = entete(ctx, p.diapo.titre, ETIQUETTES.sommaire);
    const items = p.diapo.points.slice(0, 6);
    const deux = items.length > 3;
    items.forEach((it, i) => {
      const col = deux ? i % 2 : 0;
      const lig = deux ? Math.floor(i / 2) : i;
      const w = deux ? 5.75 : 11.9;
      const x = MX + col * 6.15;
      const y = y0 + 0.15 + lig * 1.5;
      carte(ctx, x, y, w, 1.2, i);
      pastilleRonde(ctx, x + 0.75, y + 0.6, 0.72, i);
      ctx.els.push({ t: "texte", x: x + 1.35, y: y + 0.1, w: w - 1.55, h: 1.0, texte: it, taille: 18, couleur: ctx.k.encre, police: pt(ctx), gras: true, valign: "middle" });
    });
  },
  objectifs(ctx, p) {
    const y0 = entete(ctx, p.diapo.titre, ETIQUETTES.objectifs);
    const items = p.diapo.points.slice(0, 4);
    const h = Math.min(1.25, (6.7 - y0) / Math.max(items.length, 1) - 0.2);
    items.forEach((it, i) => {
      const y = y0 + 0.1 + i * (h + 0.2);
      carte(ctx, MX, y, 11.9, h, i);
      pastilleRonde(ctx, MX + 0.7, y + h / 2, Math.min(0.75, h * 0.7), i, { picto: "valider" });
      ctx.els.push({ t: "texte", x: MX + 1.35, y: y + 0.05, w: 10.3, h: h - 0.1, texte: it, taille: 19, couleur: ctx.k.encre, police: pt(ctx), valign: "middle" });
    });
  },
  notion_cle(ctx, p) {
    const etiquette = ETIQUETTES[p.diapo.type] ?? "NOTION CLÉ";
    const y0 = entete(ctx, p.diapo.titre, etiquette, p.diapo.type === "vigilance" ? ctx.t.c.alerte : undefined);
    const wTexte = 5.6;
    listePoints(ctx, MX + (ctx.g.id === "classique-pro" ? 0.2 : 0), y0 + 0.2, wTexte, 6.75 - y0 - 0.2, p.diapo.points.slice(0, 4));
    const w = 6.0;
    const h = (w * 9) / 16;
    emplacementVisuel(ctx, p, L - MX - w, y0 + 0.15, w, h);
    if (p.legende) ctx.els.push({ t: "texte", x: L - MX - w, y: y0 + 0.15 + h + 0.12, w, h: 0.5, texte: p.legende, taille: 12, italique: true, couleur: ctx.k.douce, police: pt(ctx) });
  },
  visuel_plein(ctx, p) {
    const { k } = ctx;
    ctx.els.push({ t: "texte", x: MX, y: 0.3, w: 11.9, h: 0.6, texte: p.diapo.titre, taille: 24, gras: true, couleur: k.titre, police: tp(ctx) });
    const w = 10.0;
    const h = (w * 9) / 16;
    emplacementVisuel(ctx, p, (L - w) / 2, 0.98, w, h);
    if (p.legende) ctx.els.push({ t: "texte", x: (L - w) / 2, y: 0.98 + h + 0.06, w, h: 0.36, texte: p.legende, taille: 12, italique: true, couleur: k.douce, police: pt(ctx), align: "center" });
  },
  comparaison(ctx, p) {
    const y0 = entete(ctx, p.diapo.titre, "COMPARAISON");
    const cols = p.diapo.colonnes?.length === 2 ? p.diapo.colonnes : [
      { titre: "Avant", points: p.diapo.points.slice(0, Math.ceil(p.diapo.points.length / 2)) },
      { titre: "Après", points: p.diapo.points.slice(Math.ceil(p.diapo.points.length / 2)) },
    ];
    const w = 5.55;
    const h = 6.65 - y0;
    cols.forEach((c, i) => {
      const x = MX + i * (w + 0.8);
      const coul = couleurSerie(ctx.t, i);
      carte(ctx, x, y0 + 0.05, w, h, i, { teinte: true });
      const pp = pastille(coul, "#16163A");
      ctx.els.push({ t: "rect", x: x + 0.3, y: y0 + 0.3, w: w - 0.6, h: 0.62, fond: pp.fond, rayon: 0.12 });
      ctx.els.push({ t: "texte", x: x + 0.3, y: y0 + 0.3, w: w - 0.6, h: 0.62, texte: c.titre, taille: 19, gras: true, couleur: pp.texte, police: tp(ctx), align: "center", valign: "middle" });
      listePoints(ctx, x + 0.35, y0 + 1.15, w - 0.7, h - 1.3, c.points.slice(0, 4), { taille: 17 });
    });
    const vs = pastille(ctx.t.c.accent, "#16163A");
    ctx.els.push({ t: "ellipse", x: L / 2 - 0.4, y: y0 + h / 2 - 0.35, w: 0.8, h: 0.8, fond: vs.fond });
    ctx.els.push({ t: "texte", x: L / 2 - 0.4, y: y0 + h / 2 - 0.35, w: 0.8, h: 0.8, texte: "VS", taille: 16, gras: true, couleur: vs.texte, police: tp(ctx), align: "center", valign: "middle" });
  },
  chiffres_cles(ctx, p) {
    const y0 = entete(ctx, p.diapo.titre, "CHIFFRES CLÉS");
    const ch = (p.diapo.chiffres ?? []).slice(0, 4);
    const n = Math.max(ch.length, 1);
    const w = (11.9 - (n - 1) * 0.35) / n;
    ch.forEach((c, i) => {
      const x = MX + i * (w + 0.35);
      carte(ctx, x, y0 + 0.2, w, 3.9, i);
      ctx.els.push({ t: "texte", x: x + 0.2, y: y0 + 0.65, w: w - 0.4, h: 1.5, texte: c.valeur, taille: c.valeur.length > 8 ? 36 : 54, gras: true, couleur: pastille(couleurSerie(ctx.t, i), ctx.k.surface).fond === couleurSerie(ctx.t, i) ? couleurSerie(ctx.t, i) : assombrir(couleurSerie(ctx.t, i), 0.25), police: tp(ctx), align: "center", valign: "middle" });
      ctx.els.push({ t: "texte", x: x + 0.25, y: y0 + 2.25, w: w - 0.5, h: 1.7, texte: c.libelle, taille: 17, couleur: ctx.k.encre, police: pt(ctx), align: "center", valign: "top" });
    });
    const srcs = ch.filter((c) => c.source).map((c) => `[S${c.source}]`);
    if (srcs.length) ctx.els.push({ t: "texte", x: MX, y: y0 + 4.25, w: 11.9, h: 0.4, texte: `Source : ${[...new Set(srcs)].join(", ")} — voir la diapositive « Sources ».`, taille: 12, couleur: ctx.k.douce, police: pt(ctx) });
  },
  processus(ctx, p) {
    const y0 = entete(ctx, p.diapo.titre, "ÉTAPES");
    const items = p.diapo.points.slice(0, 5);
    const n = Math.max(items.length, 1);
    const w = (11.9 - (n - 1) * 0.4) / n;
    items.forEach((it, i) => {
      const x = MX + i * (w + 0.4);
      carte(ctx, x, y0 + 0.75, w, 3.8, i);
      pastilleRonde(ctx, x + w / 2, y0 + 0.75, 0.95, i, { picto: pictoPour("", it), texte: String(i + 1) });
      ctx.els.push({ t: "texte", x: x + 0.12, y: y0 + 1.4, w: w - 0.24, h: 0.45, texte: `Étape ${i + 1}`, taille: 12, gras: true, couleur: ctx.k.douce, police: tp(ctx), align: "center", espacement: 1 });
      ctx.els.push({ t: "texte", x: x + 0.15, y: y0 + 1.85, w: w - 0.3, h: 2.5, texte: it, taille: n > 4 ? 15 : 17, couleur: ctx.k.encre, police: pt(ctx), align: "center", valign: "top" });
      if (i < n - 1) ctx.els.push({ t: "ligne", x1: x + w + 0.05, y1: y0 + 2.6, x2: x + w + 0.35, y2: y0 + 2.6, couleur: ctx.t.c.accent, epaisseur: 3, fleche: true });
    });
  },
  a_retenir(ctx, p) {
    const vigilance = p.diapo.type === "vigilance";
    const etiquette = ETIQUETTES[p.diapo.type] ?? "À RETENIR";
    const y0 = entete(ctx, p.diapo.titre, etiquette, vigilance ? ctx.t.c.alerte : ctx.t.c.accent);
    const c = vigilance ? ctx.t.c.alerte : ctx.t.c.primaire;
    const h = 6.55 - y0;
    ctx.els.push({ t: "rect", x: MX, y: y0 + 0.1, w: 11.9, h, fond: ctx.k.sombre ? ctx.k.surface : eclaircir(c, 0.9), rayon: ctx.g.rayon, contour: ctx.k.sombre ? c : undefined, epaisseur: 1.5 });
    ctx.els.push({ t: "rect", x: MX, y: y0 + 0.1, w: 0.14, h, fond: c });
    pastilleRonde(ctx, MX + 0.95, y0 + 0.95, 1.0, 0, { picto: vigilance ? "alerte" : "cle", couleur: c });
    listePoints(ctx, MX + 1.8, y0 + 0.45, 9.8, h - 0.6, p.diapo.points.slice(0, 4), { taille: Math.min(22, corpsPour(p.diapo.points.join(" ").split(/\s+/).length, 21)) });
  },
  activite(ctx, p) {
    const y0 = entete(ctx, p.diapo.titre, ETIQUETTES[p.diapo.type] ?? "ACTIVITÉ", ctx.t.c.succes);
    const c = ctx.t.c.succes;
    const pp = pastille(c, "#16163A");
    ctx.els.push({ t: "rect", x: MX, y: y0 + 0.1, w: 3.3, h: 6.55 - y0, fond: pp.fond, rayon: ctx.g.rayon });
    ctx.els.push({ t: "picto", x: MX + 1.0, y: y0 + 0.6, taille: 1.3, picto: "outil", couleur: pp.texte });
    ctx.els.push({ t: "texte", x: MX + 0.1, y: y0 + 2.1, w: 3.1, h: 1.6, texte: "À vous de jouer !", taille: 20, gras: true, couleur: pp.texte, police: tp(ctx), align: "center", valign: "top" });
    carte(ctx, MX + 3.6, y0 + 0.1, 8.3, 6.55 - y0, 3);
    listePoints(ctx, MX + 3.95, y0 + 0.45, 7.7, 6.55 - y0 - 0.6, p.diapo.points.slice(0, 5), { numero: true });
  },
  quiz(ctx, p) {
    const y0 = entete(ctx, p.diapo.titre, ETIQUETTES[p.diapo.type] ?? "QUIZ ÉCLAIR", ctx.t.c.secondaire);
    const items = p.diapo.points.slice(0, 5);
    const deux = items.length > 3;
    const lignes = deux ? Math.ceil(items.length / 2) : items.length;
    const h = Math.min(1.35, (6.6 - y0) / lignes - 0.2);
    items.forEach((it, i) => {
      const col = deux ? i % 2 : 0;
      const lig = deux ? Math.floor(i / 2) : i;
      const w = deux ? 5.75 : 11.9;
      const x = MX + col * 6.15;
      const y = y0 + 0.1 + lig * (h + 0.2);
      carte(ctx, x, y, w, h, i + 1);
      pastilleRonde(ctx, x + 0.6, y + h / 2, Math.min(0.7, h * 0.7), i + 1, { texte: `Q${i + 1}` });
      ctx.els.push({ t: "texte", x: x + 1.15, y: y + 0.05, w: w - 1.3, h: h - 0.1, texte: it, taille: 17, couleur: ctx.k.encre, police: pt(ctx), valign: "middle" });
    });
  },
  synthese(ctx, p) {
    const y0 = entete(ctx, p.diapo.titre, ETIQUETTES.synthese, ctx.t.c.accent);
    const items = p.diapo.points.slice(0, 6);
    const cols = items.length > 4 ? 3 : 2;
    const lignes = Math.ceil(items.length / cols);
    const w = (11.9 - (cols - 1) * 0.3) / cols;
    const h = Math.min(2.2, (6.6 - y0) / Math.max(lignes, 1) - 0.25);
    items.forEach((it, i) => {
      const x = MX + (i % cols) * (w + 0.3);
      const y = y0 + 0.1 + Math.floor(i / cols) * (h + 0.25);
      carte(ctx, x, y, w, h, i, { teinte: ctx.g.cartes !== "verre" });
      pastilleRonde(ctx, x + 0.55, y + 0.6, 0.6, i, { picto: "valider" });
      ctx.els.push({ t: "texte", x: x + 0.25, y: y + 1.0, w: w - 0.5, h: h - 1.1, texte: it, taille: 16, couleur: ctx.k.encre, police: pt(ctx), valign: "top" });
    });
  },
  sources(ctx, _p, sources) {
    const y0 = entete(ctx, "Sources", "RÉFÉRENCES");
    sources.slice(0, 8).forEach((s, i) => {
      const y = y0 + 0.1 + i * 0.62;
      ctx.els.push({ t: "texte", x: MX, y, w: 0.8, h: 0.5, texte: `S${s.rang}`, taille: 13, gras: true, couleur: pastille(ctx.t.c.primaire, "#16163A").texte, police: tp(ctx), align: "center", valign: "middle" });
      ctx.els.splice(ctx.els.length - 1, 0, { t: "rect", x: MX, y: y + 0.06, w: 0.8, h: 0.38, fond: pastille(ctx.t.c.primaire, "#16163A").fond, rayon: 0.19 });
      ctx.els.push({ t: "texte", x: MX + 1.0, y, w: 10.9, h: 0.5, texte: `${s.titre}${s.editeur ? ` — ${s.editeur}` : ""}`, taille: 14, couleur: ctx.k.sombre ? "#9CCBFF" : ctx.t.c.primaire_texte, police: pt(ctx), valign: "middle", lien: s.url });
    });
  },
  fin(ctx) {
    const { c, g, k, t } = ctx;
    const clair = g.id === "atelier-pratique";
    const encre = clair ? k.encre : "#FFFFFF";
    ctx.els.push({ t: "texte", x: MX + 0.3, y: 2.0, w: 8.5, h: 1.4, texte: "Merci !", taille: 60, gras: true, couleur: encre, police: tp(ctx) });
    ctx.els.push({ t: "texte", x: MX + 0.3, y: 3.5, w: 8.5, h: 0.7, texte: "Vos questions, vos retours : on en parle.", taille: 22, couleur: encre, police: pt(ctx) });
    ctx.els.push({ t: "texte", x: MX + 0.3, y: 5.3, w: 8.5, h: 1.0, texte: [c.organisme, c.contact].filter(Boolean), taille: 15, couleur: encre, police: pt(ctx) });
    if (c.logo) ctx.els.push({ t: "image", x: L - MX - 2.0, y: 6.3, w: 1.8, h: 0.75, ref: "logo" });
    void t;
  },
};

/** Pages pleines (couverture, intercalaire, fin) : décor plein cadre, pas d'en-tête ni de pied. */
const PLEINES: readonly MiseEnPage[] = ["couverture", "chapitre", "fin"];

export function composerPage(g: Gabarit, t: Theme, p: Page, c: ContexteSupport, numero: number, total: number, sources: readonly SourcePage[] = []): Scene {
  const k = couleursSupport(g, t);
  const ctx: Ctx = { g, t, k, els: [], c, numero, total };
  const pleine = PLEINES.includes(p.mise_en_page);
  if (g.id === "classique-pro" && !pleine) ctx.els.push({ t: "rect", x: 0, y: 0, w: 0.22, h: H, fond: t.c.primaire });
  COMPOSITEURS[p.mise_en_page](ctx, p, sources);
  if (!pleine) pied(ctx);
  const decor = pleine ? `${g.id}:${p.mise_en_page === "fin" ? "couverture" : p.mise_en_page}` : `${g.id}:contenu`;
  return { fond: k.fond, decor, elements: ctx.els, notes: p.diapo.notes };
}

// ——— Décors (SVG 1333 × 750 px, 100 px par pouce) : rendus en PNG par le serveur pour le PPTX ———

const iso = (x: number, y: number, s: number, hBloc: number, c: string, opacite = 1) =>
  `<g opacity="${opacite}"><polygon points="${x},${y} ${x + s},${y - s / 2} ${x + 2 * s},${y} ${x + s},${y + s / 2}" fill="${eclaircir(c, 0.15)}"/><polygon points="${x},${y} ${x + s},${y + s / 2} ${x + s},${y + s / 2 + hBloc} ${x},${y + hBloc}" fill="${c}"/><polygon points="${x + s},${y + s / 2} ${x + 2 * s},${y} ${x + 2 * s},${y + hBloc} ${x + s},${y + s / 2 + hBloc}" fill="${assombrir(c, 0.28)}"/></g>`;

/** Décor d'un gabarit pour un rôle de page (« couverture », « chapitre », « contenu »). */
export function decorSvg(id: string, t: Theme): string {
  const [gid, role] = id.split(":") as [IdGabarit, string];
  const g = GABARITS[gid] ?? GABARITS["moderne-vif"];
  const k = couleursSupport(g, t);
  const [c0, c1, c2, c3] = [couleurSerie(t, 0), couleurSerie(t, 1), couleurSerie(t, 2), couleurSerie(t, 3)];
  const W = 1333;
  const Hp = 750;
  const ouvrir = `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${Hp}" viewBox="0 0 ${W} ${Hp}"><defs><linearGradient id="gp" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${assombrir(c0, 0.1)}"/><stop offset="1" stop-color="${assombrir(c1, 0.25)}"/></linearGradient><linearGradient id="gn" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${k.fond}"/><stop offset="1" stop-color="${melanger(k.fond, c0, 0.25)}"/></linearGradient>${[c0, c1, c2, c3].map((c, i) => `<radialGradient id="h${i}"><stop offset="0" stop-color="${c}" stop-opacity="0.9"/><stop offset="0.55" stop-color="${c}" stop-opacity="0.45"/><stop offset="1" stop-color="${c}" stop-opacity="0"/></radialGradient>`).join("")}</defs>`;
  // Halo flou sans filtre (dégradé radial) : même effet qu'un flou gaussien, rendu dix fois plus rapide.
  const halo = (cx: number, cy: number, r: number, i: number, opacite: number) => `<circle cx="${cx}" cy="${cy}" r="${r * 1.5}" fill="url(#h${i})" opacity="${opacite}"/>`;
  const fermer = "</svg>";
  const pleine = role !== "contenu";
  const lisible = (c: string) => (texteSur(c) === "#FFFFFF" ? c : assombrir(c, 0.35));
  switch (g.id) {
    case "classique-pro":
      return pleine
        ? `${ouvrir}<rect width="${W}" height="${Hp}" fill="${lisible(t.c.primaire)}"/><rect x="0" y="0" width="22" height="${Hp}" fill="${t.c.accent}"/>${Array.from({ length: 9 }, (_, i) => `<line x1="${860 + i * 55}" y1="0" x2="${560 + i * 55}" y2="${Hp}" stroke="#FFFFFF" stroke-opacity="0.08" stroke-width="2"/>`).join("")}<rect x="100" y="${Hp - 120}" width="140" height="6" fill="${t.c.accent}"/>${fermer}`
        : `${ouvrir}<rect width="${W}" height="${Hp}" fill="${k.fond}"/>${fermer}`;
    case "moderne-vif":
      return pleine
        ? `${ouvrir}<rect width="${W}" height="${Hp}" fill="url(#gp)"/>${halo(1150, 140, 260, 2, 0.6)}${halo(1010, 640, 230, 1, 0.65)}<circle cx="1080" cy="360" r="170" fill="#FFFFFF" opacity="0.12"/><circle cx="1210" cy="560" r="90" fill="${c2}" opacity="0.9"/><circle cx="960" cy="200" r="46" fill="${c3}" opacity="0.95"/><rect x="900" y="420" width="150" height="150" rx="36" fill="#FFFFFF" opacity="0.18" transform="rotate(18 975 495)"/>${fermer}`
        : `${ouvrir}<rect width="${W}" height="${Hp}" fill="${k.fond}"/><circle cx="1290" cy="20" r="120" fill="${c1}" opacity="0.22"/><circle cx="1240" cy="-10" r="70" fill="${c2}" opacity="0.5"/><rect x="0" y="${Hp - 8}" width="${W}" height="8" fill="url(#gp)"/>${fermer}`;
    case "atelier-pratique":
      return pleine
        ? `${ouvrir}<rect width="${W}" height="${Hp}" fill="${k.fond}"/><rect x="820" y="90" width="330" height="330" rx="10" fill="${eclaircir(c2, 0.35)}" transform="rotate(6 985 255)"/><rect x="930" y="330" width="290" height="290" rx="10" fill="${eclaircir(c1, 0.3)}" transform="rotate(-8 1075 475)"/><rect x="760" y="420" width="250" height="250" rx="10" fill="${eclaircir(c3, 0.3)}" transform="rotate(4 885 545)"/><rect x="930" y="60" width="140" height="40" fill="${eclaircir(c0, 0.5)}" opacity="0.85" transform="rotate(-6 1000 80)"/><rect x="0" y="0" width="${W}" height="16" fill="${c0}"/>${fermer}`
        : `${ouvrir}<rect width="${W}" height="${Hp}" fill="${k.fond}"/><rect x="0" y="0" width="${W}" height="10" fill="${c0}"/><rect x="${W - 150}" y="26" width="110" height="30" fill="${eclaircir(c2, 0.35)}" transform="rotate(8 ${W - 95} 41)"/>${fermer}`;
    case "immersif-3d": {
      const blocs = [iso(900, 300, 90, 120, c0), iso(1080, 390, 70, 90, c1), iso(980, 470, 60, 140, c2), iso(1160, 230, 50, 70, c3), iso(820, 520, 40, 60, c1, 0.8)].join("");
      const grille = Array.from({ length: 14 }, (_, i) => `<line x1="${i * 100}" y1="0" x2="${i * 100 - 300}" y2="${Hp}" stroke="${c0}" stroke-opacity="0.07"/>`).join("");
      return pleine
        ? `${ouvrir}<rect width="${W}" height="${Hp}" fill="url(#gn)"/>${grille}${halo(1050, 380, 300, 0, 0.32)}${blocs}${fermer}`
        : `${ouvrir}<rect width="${W}" height="${Hp}" fill="url(#gn)"/>${grille}${halo(1260, 40, 160, 1, 0.3)}${iso(1190, 70, 30, 40, c0, 0.85)}${iso(1250, 110, 22, 30, c2, 0.85)}${fermer}`;
    }
  }
}
